import type { TFunction } from 'i18next'
import type { ActivityEntry } from '@proaxiv/server-api'

/**
 * Resolves a localized label for one {@link ActivityEntry} in the participant conversation
 * history (see `ParticipantConversationModal`, issue #158 / #259).
 *
 * Entries written after this feature landed carry `type` + machine-readable `params` (mirroring
 * how in-app notifications resolve copy, see `formatNotification.ts`) and are resolved purely from
 * those two fields. Legacy rows written before `params` existed fall back to matching the stored
 * English `message` against the exact strings `ParticipantActivityLog` used to write, so old
 * history still renders through i18n instead of raw English.
 */
export function formatActivityMessage(entry: ActivityEntry, t: TFunction): string {
  const params = entry.params ?? undefined
  const variant = params?.variant

  switch (entry.type) {
    case 'INVITED':
      return t('activity_invited')
    case 'INVITATION_RESENT':
      return t('activity_invitation_resent')
    case 'REGISTERED':
      return (variant ?? legacyVariant(entry.message)) === 'resubmitted'
        ? t('activity_registered_resubmitted')
        : t('activity_registered')
    case 'REGISTERED_BY_ORGANIZER':
      return t('activity_registered_by_organiser')
    case 'REGISTRATION_ACCEPTED':
      return t('activity_registration_accepted')
    case 'REGISTRATION_REJECTED':
      return t('activity_registration_rejected')
    case 'REGISTRATION_REOPENED':
      return t('activity_registration_reopened')
    case 'ROLE_CHANGED': {
      const role = params?.role ?? legacyRole(entry.message)
      return t('activity_role_changed', { role: role ? t(`role_${role.toLowerCase()}`) : '' })
    }
    case 'PRESENTATION_REQUESTED':
      return t('activity_presentation_requested')
    case 'PRESENTATION_UPLOADED':
      return t('activity_presentation_uploaded')
    case 'PRESENTATION_ACCEPTED':
      return (variant ?? legacyVariant(entry.message)) === 'organiser_backup'
        ? t('activity_presentation_accepted_organiser_backup')
        : t('activity_presentation_accepted')
    case 'PRESENTATION_REJECTED':
      return t('activity_presentation_rejected')
    case 'REMINDER_SENT':
      return t('activity_reminder_sent')
    case 'CERTIFICATE_GENERATED':
      return (variant ?? legacyVariant(entry.message)) === 'regenerated'
        ? t('activity_certificate_regenerated')
        : t('activity_certificate_generated')
    case 'CERTIFICATE_SENT':
      return (variant ?? legacyVariant(entry.message)) === 'resent'
        ? t('activity_certificate_resent')
        : t('activity_certificate_generated_and_sent')
    case 'MEDIA_NOTIFICATION_SENT':
      return (variant ?? legacyVariant(entry.message)) === 'resent'
        ? t('activity_media_notification_resent')
        : t('activity_media_notification_sent')
    case 'ATTENDANCE_MARKED':
      // 'checkin' = recorded through the check-in flow (kiosk QR scan / staff check-in);
      // no variant = the organiser's manual post-event override (batch MARK_ATTENDED).
      return variant === 'checkin'
        ? t('activity_attendance_checked_in')
        : t('activity_attendance_marked')
    case 'ATTENDANCE_CLEARED':
      return t('activity_attendance_cleared')
    case 'NOTE_UPDATED':
      return (variant ?? legacyVariant(entry.message)) === 'cleared'
        ? t('activity_note_cleared')
        : t('activity_note_updated')
    default:
      // Unknown/future type without a matching key — better to show the stored English summary
      // than nothing at all.
      return entry.message
  }
}

/**
 * Infers the `variant`/`role` param for entries written before `params` existed, by matching the
 * exact English strings `ParticipantActivityLog` wrote for each type. Keep in sync with those
 * literals — this table only exists to keep old history readable, never for new writes.
 */
const LEGACY_VARIANT_BY_MESSAGE: Record<string, string> = {
  'Registration resubmitted after rejection, pending re-evaluation': 'resubmitted',
  'Presentation uploaded and automatically accepted (organiser backup)': 'organiser_backup',
  'Certificate of Attendance regenerated (participant or event details changed)': 'regenerated',
  'Certificate of Attendance re-sent': 'resent',
  'Event media notification re-sent': 'resent',
  'Private note cleared': 'cleared',
}

function legacyVariant(message: string): string | undefined {
  return LEGACY_VARIANT_BY_MESSAGE[message]
}

const LEGACY_ROLE_PREFIX = 'Role changed to '
const LEGACY_ROLE_BY_LABEL: Record<string, string> = {
  Audience: 'AUDIENCE',
  Speaker: 'SPEAKER',
  Staff: 'STAFF',
  Host: 'HOST',
}

function legacyRole(message: string): string | undefined {
  if (!message.startsWith(LEGACY_ROLE_PREFIX)) return undefined
  return LEGACY_ROLE_BY_LABEL[message.slice(LEGACY_ROLE_PREFIX.length)]
}
